/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo} from 'react'
import PropTypes from 'prop-types'

// Project Components
import Breadcrumb from './index'
import BreadcrumbItem from './breadcrumb-item'

// Others
import {categoryUrlBuilder} from '../../utils/url'
import {CATALOG_ROOT_CATEGORY_ID} from '../../constants'

/**
 * A simplification of the Chakra `Breadcrumb` component for our project needs. Given
 * a list of categories and an optional product, display a breadcrumb and it's items.
 * When all breadcrumb items are too long for the screen only the last item is shown
 * and 3 extra dots are added on the left side for showing all breadrumbs.
 */
const CategoriesBreadcrumb = ({categories, ...props}) => {
    const items = useMemo(
        () =>
            categories
                .filter((category) => category.id !== CATALOG_ROOT_CATEGORY_ID)
                .map((category) => ({
                    id: category.id,
                    name: category.name,
                    href: categoryUrlBuilder(category)
                })),
        [categories]
    )

    return (
        <Breadcrumb {...props}>
            {items.map(({id, name, ...props}) => (
                <BreadcrumbItem {...props} key={id} data-testid="sf-crumb-item">
                    {name}
                </BreadcrumbItem>
            ))}
        </Breadcrumb>
    )
}

CategoriesBreadcrumb.displayName = 'CategoriesBreadcrumb'

CategoriesBreadcrumb.propTypes = {
    /**
     * The categories to be displayed in this breadcrumb.
     */
    categories: PropTypes.array
}

export default CategoriesBreadcrumb
